import React, { useEffect, useState } from 'react';
import PageContainer from '../../components/PageContainer';
import MainContent from '../../components/MainContent';
import PageTitle from '../../components/Common/PageTitle/PageTitle';
import ActionDropdownItem from '../../components/Common/PageTitle/ActionDropdownItem';
import { useTheme } from '../../DataStore/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useBanking } from '../../DataStore/BankingContext';
import { useCurrency } from '../../util/SystemVariables';
import RecentTransactions from '../../components/Banking/RecentTransactions';
import ManageBankModal from '../../components/Banking/ManageBankModal';
import NewBankModal from '../../components/Banking/NewBankModal';
import AlertDeleteBank from '../../components/Banking/AlertDeleteBank';
import UpdateBankModal from '../../components/Banking/UpdateBankModal';
import Notification from '../../components/Common/Notification';
import { useAccountReport } from '../../DataStore/AccountReportContext';
import NewChequeTransactionModal from '../../components/Banking/NewBankTransactionModal';
import MiniStatCard from '../../components/Banking/MiniStatCard';
import { numberFormat } from '../../util/araknerdlib';
import NexumPaySummaryCard from '../../components/Banking/NexumPaySummaryCard';
import AccountsRow from '../../components/Banking/AccountsRow';


const BankingPage = () => {
  const { openSettings, setNotify } = useTheme();  
  const { bankListing, getBanks, getRecentTransactions, getQuickStats, quickStats } = useBanking();
  const [showManageBank, setShowManageBank] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [showNewBank, setShowNewBank] = useState(null);
  const [showEditBank, setShowEditBank] = useState(null);
  const [showDeleteBank, setShowDeleteBank] = useState(null);
  const [showNewCheque, setShowNewCheque] = useState(false);
  const [txType, setTxType] = useState("Deposit")
  const currency = useCurrency();

  const {dispatch: accountReportDispatch} = useAccountReport();
  const navigate = useNavigate();

  useEffect(() => {
    getBanks();
    getQuickStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ------------ actions ------------ */
  const handleNewBank = () => {
    setSelectedBank(null);
    setShowNewBank(true)
  }
  const handleImportStmt = () => openSettings('Import Bank Statement', 'BankImportWizard');
  const handleNewTransfer = () => {navigate('/asset-contra-new');}
  const handleReconcile = () => {navigate('/banking-transactions');}
  const handleNewDeposit = (bank) => {
     if(!bank.account_id){
      setNotify(<Notification type='info' message="This bank is not currently linked to a ledger account. Please link account first, then try again."/>)
      return;
    }
    setSelectedBank(bank);
    setTxType("Deposit");
    setShowNewCheque(true);
  }
  const handleNewWithdraw = (bank) => {
     if(!bank.account_id){
      setNotify(<Notification type='info' message="This bank is not currently linked to a ledger account. Please link account first, then try again."/>)
      return;
    }
    setSelectedBank(bank);
    setTxType("Withdraw");
    setShowNewCheque(true);
  }
  const PageActions = () => (
    <>
      <ActionDropdownItem iconClass="fa fa-edit" label="New Bank" onClick={handleNewBank} />
      <ActionDropdownItem iconClass="fa fa-file-import" label="Import Statement" onClick={handleImportStmt} />
      <ActionDropdownItem iconClass="fa fa-sync" label="Refresh" onClick={handleRefresh}/>
    </>
  );

  const isLoading = !Array.isArray(bankListing) && bankListing !== '404';

  const handleManageBank = (bank) => {
    setSelectedBank(bank);
    setShowManageBank(true);
  }

  const handleEditBank= (bank) => {
    setSelectedBank(bank);
    setShowEditBank(true);
  }

  const handleRefresh=async()=>{
    await getBanks(true)
    await getRecentTransactions(true);
    await getQuickStats(true);
  }

  const handleDeleteBank = (bank)=>{
    setSelectedBank(bank);
    setShowDeleteBank(true);
  }

  const handleStatement = (bank)=>{
    if(!bank.account_id){
      setNotify(<Notification type='info' message="This bank is not currently linked to a ledger account. Please link account first, then try again."/>)
      return;
    }

    accountReportDispatch({type:'accountInfo', payload: bank?.account_info});
    navigate('/report-account-statement');
  }

  return (
    <>
    <PageContainer>
      <MainContent>
        <PageTitle
          title="Banking"
          description="Receive and create cheques, track your bank statements, and manage your cheques."
          iconClass="fa fa-credit-card"
          PageActions={PageActions}
        />

        <div className="row">
          <div className="col-xl-8 mb-3">
            {/* Accounts card */}
            <AccountsRow 
              isLoading={isLoading}
              bankListing={bankListing}
              currency={currency}
              handleNewTransfer={handleNewTransfer}
              handleReconcile={handleReconcile}
              handleEditBank={handleEditBank}
              handleNewDeposit={handleNewDeposit}
              handleNewWithdraw={handleNewWithdraw}
              handleManageBank={handleManageBank}
              handleDeleteBank={handleDeleteBank}
              handleStatement={handleStatement}
            />

            {/* Recent transactions */}
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <RecentTransactions currency={currency} />
              </div>
            </div>
          </div>

          {/* RIGHT: Quick actions + NexumPay */}
          <div className="col-xl-4 mb-3">

             <div className="row">
              <div className="col-12 mb-1">
                <MiniStatCard
                  icon="fa-wallet"
                  label="Actual Bank Balance"
                  value={`${currency} ${numberFormat(quickStats?.actual_amount || 0)}`}
                  tone="primary"
                  meta="Actual"
                  hint="Sum of all cash-at-bank ledger balances."
                  loading={!quickStats}
                />
              </div>

              <div className="col-12 mb-1">
                <MiniStatCard
                  icon="fa-check-circle"
                  label="UnCleared Funds"
                  value={`${currency} ${numberFormat(quickStats?.pending_amount || 0)}`}
                  tone="danger"
                  meta="Pending"
                  hint="Funds not yet cleared by the bank."
                  loading={!quickStats}
                />
              </div>

              <div className="col-12 mb-3">
                <MiniStatCard
                  icon="fa-hourglass-half"
                  label="Provisional Balance"
                  value={`${currency} ${numberFormat(quickStats?.provisional_amount || 0)}`}
                  tone="warning"
                  meta="Available"
                  hint="Balance after reconciliations and pending items."
                  loading={!quickStats}
                />
              </div>
            </div>


            <NexumPaySummaryCard currency={currency} isConnected={false}/>

            <div className="card border-0 shadow-sm mt-1">
              <div className="card-body d-flex flex-column">
                <div className="h6">Quick Actions</div>
                <div className="d-flex flex-wrap" style={{ gap: 8 }}>
               
                  <button className="btn btn-light btn-sm" onClick={handleNewTransfer}>
                    <i className="fa fa-random mr-1" />
                    New Transfer
                  </button>
                  <button className="btn btn-light btn-sm" onClick={handleReconcile}>
                    <i className="fa fa-balance-scale mr-1" />
                    Reconcile
                  </button>
                  <button className="btn btn-light btn-sm" onClick={handleNewBank}>
                    <i className="fa fa-university mr-1" />
                    Add Bank
                  </button>
                </div>


                <div className="mt-3 pt-2 small text-muted">
                  Tips: Reconcile daily to catch discrepancies early.
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainContent>
    </PageContainer>
     <ManageBankModal
        show={showManageBank}
        onClose={() => setShowManageBank(false)}
        onSave={handleRefresh}
        bank={selectedBank}
      />
      <NewBankModal
        show={showNewBank}
        onClose={()=>setShowNewBank(false)}
        onSave={handleRefresh}
        />

      <UpdateBankModal
        show={showEditBank}
        onClose={()=>setShowEditBank(false)}
        onSave={handleRefresh}
        bank={selectedBank}
        />

      <AlertDeleteBank
        show={showDeleteBank}
        onClose={()=>setShowDeleteBank(false)}
        bank={selectedBank}
        />
        <NewChequeTransactionModal
          show={showNewCheque}
          onClose={() => setShowNewCheque(false)}
          onSaved={handleRefresh}
          bank={selectedBank}
          txnType={txType}
          currency={currency}
        />
    </>
  );
};

export default BankingPage;
